import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ResearchPage.module.css';

export default function ResearchPage({ data }) {
  const navigate = useNavigate();
  const publications = data.publications?.items || data.publications || [];

  // Helper function to bold your name in the academic author list
  const formatAuthors = (authorString) => {
    if (!authorString) return null;
    const parts = authorString.split(/(Banarjee, J\.|Jolok, B\.|Jolok Banarjee|J\. Banarjee)/g);

    return parts.map((part, index) =>
      (part === 'Banarjee, J.' || part === 'Jolok, B.' || part === 'Jolok Banarjee' || part === 'J. Banarjee')
        ? <span key={index} className={styles.highlightAuthor}>{part}</span>
        : part
    );
  };

  // Helper for dynamic status coloring
  const getStatusClass = (status) => {
    if (!status) return '';
    const lower = status.toLowerCase();
    if (lower.includes('submitted') || lower.includes('review') || lower.includes('draft') || lower.includes('preprint')) return styles.statusSubmitted;
    if (lower.includes('accepted')) return styles.statusAccepted;
    if (lower.includes('published')) return styles.statusPublished;
    return '';
  };

  // SMART ROUTER: Bypasses missing JSON data entirely by checking the title!
  const handleReadMethodology = (pub) => {
    const title = pub.title || "";

    if (pub.slug && pub.slug !== "undefined") {
      navigate(`/research/${pub.slug}`);
    } else if (title.includes("End-to-End") || title.includes("Integrated Framework for Turbofan")) {
      navigate('/research/e2e-pdm-framework');
    } else if (title.includes("Sustainable Additive") || title.includes("rPET")) {
      navigate('/research/rpet-additive-manufacturing');
    } else if (title.includes("Anomaly Detection Framework")) {
      navigate('/research/ai-anomaly-detection');
    } else if (title.includes("Supersonic")) {
      navigate('/research/supersonic-wing-analysis');
    } else {
      navigate('/research/details', { state: { publication: pub } });
    }
  };

  return (
    <div className={styles.container}>

      {/* Publication Index List */}
      <div className={styles.list}>
        {publications.length === 0 ? (
          <p>No publications available.</p>
        ) : (
          publications.map((pub, idx) => {

            // Smart DOI Extractor: Checks if the link is actually a DOI URL
            const isLinkDOI = pub.link && pub.link.includes('doi.org');
            const extractedDOI = isLinkDOI ? pub.link.split('doi.org/')[1] : null;
            const displayDOI = pub.doi || extractedDOI;
            const pubYear = pub.year || pub.date;

            return (
              <article key={idx} className={styles.paperRow}>

                {/* Document Thumbnail */}
                {pub.image && pub.image.trim() !== "" && (
                  <div
                    className={styles.thumbnailWrapper}
                    onClick={() => handleReadMethodology(pub)}
                    title={`Read details: ${pub.title}`}
                  >
                    <img
                      src={pub.image}
                      alt={`Cover of ${pub.title}`}
                      className={styles.thumbnailImage}
                      loading="lazy"
                    />
                  </div>
                )}

                {/* Text Content */}
                <div className={styles.content}>

                  <div className={styles.metaTopRow}>
                    <div className={styles.metaLeftGroup}>
                      <span className={styles.paperIndex}>#{String(idx + 1).padStart(2, '0')}</span>
                      {pubYear && <span className={styles.yearPill}>{pubYear}</span>}
                      {pub.venue && <span className={styles.venue}>{pub.venue}</span>}
                    </div>

                    {pub.status && (
                      <span className={`${styles.statusBadge} ${getStatusClass(pub.status)}`}>
                        {pub.status}
                      </span>
                    )}
                  </div>

                  <h2
                    className={styles.title}
                    onClick={() => handleReadMethodology(pub)}
                  >
                    {pub.title}
                  </h2>

                  <p className={styles.authors}>
                    {formatAuthors(pub.authors)}
                  </p>

                  {/* Always-visible abstract formatted beautifully */}
                  {pub.abstract && (
                    <p className={styles.abstract}>{pub.abstract}</p>
                  )}

                  {/* Keywords if available */}
                  {Array.isArray(pub.keywords) && pub.keywords.length > 0 && (
                    <div className={styles.keywordsRow}>
                      {pub.keywords.map((kw, kIdx) => (
                        <span key={kIdx} className={styles.keywordPill}>{kw}</span>
                      ))}
                    </div>
                  )}

                  {/* Minimalist Action Buttons */}
                  <div className={styles.actionRow}>

                    <button
                      onClick={() => handleReadMethodology(pub)}
                      className={`${styles.btn} ${styles.btnPrimary}`}
                    >
                      Read Details <span aria-hidden="true">→</span>
                    </button>

                    {/* SMART DOI BUTTON: Displays DOI if it exists in 'doi' OR 'link' field */}
                    {displayDOI && (
                      <a
                        href={`https://doi.org/${displayDOI}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${styles.btn} ${styles.btnOutline}`}
                      >
                        DOI: {displayDOI} <span aria-hidden="true">↗</span>
                      </a>
                    )}

                    {/* STANDARD EXTERNAL LINK: Only shows if there is a link, and it's NOT a DOI */}
                    {pub.link && pub.link.trim() !== "" && !displayDOI && (
                      <a
                        href={pub.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${styles.btn} ${styles.btnOutline}`}
                      >
                        External Link <span aria-hidden="true">↗</span>
                      </a>
                    )}
                  </div>

                </div>
              </article>
            );
          })
        )}
      </div>

      {/* ── ACTIVE RESEARCH AREAS ── */}
      <section className={styles.areasSection}>
        <h2 className={styles.areasHeader}>
          Active Research Areas
        </h2>
        <div className={styles.areasGrid}>
          {[
            {
              title: "UAV Systems and Autonomy",
              desc: "Fault-tolerant control architectures, MAVLink/ArduPilot integration, outdoor flight testing, and Bangla voice-operated emergency-response UAV systems.",
              icon: "🛸"
            },
            {
              title: "Aircraft Diagnostics & Structural Health Monitoring",
              desc: "End-to-end machine learning frameworks for turbofan engine anomaly detection, condition monitoring, and remaining useful life (RUL) prognostics.",
              icon: "⚙️"
            },
            {
              title: "Data-Driven Manufacturing & Structural Analysis",
              desc: "Closed-loop recycled-polymer (rPET) filament extrusion, ASTM D638 mechanical characterization, and high-Mach FEA structural analysis in ANSYS Mechanical.",
              icon: "♻️"
            },
            {
              title: "Physics-Informed Machine Learning",
              desc: "Integrating physical constraints and thermodynamic anchors into deep learning and ensemble architectures for reliable aerospace engineering predictions.",
              icon: "🔬"
            },
            {
              title: "Explainable AI (XAI) for Maintenance",
              desc: "SHAP-based interpretability and live FastAPI/Streamlit deployment frameworks that turn black-box predictions into transparent maintenance decisions.",
              icon: "🧠"
            },
            {
              title: "Supersonic Aircraft Design & Sizing",
              desc: "Multidisciplinary conceptual design, constraint analysis, weight estimation, and stability evaluation for next-generation air defense interceptors.",
              icon: "✈️"
            },
          ].map((area, i) => (
            <div
              key={i}
              className={styles.areaCard}
            >
              <div className={styles.areaIconBadge}>{area.icon}</div>
              <h4 className={styles.areaTitle}>
                {area.title}
              </h4>
              <p className={styles.areaDesc}>
                {area.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}